import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { markTourProgressRemote } from 'services/toursService'
import { getWorkflowByKey, matchesRoute } from './tours'
import { PATHS } from 'utils/paths'
import { anchorSelector, waitForAnchor } from './anchors'
import { markTourSeen } from './storage'
import { RUNNING_CLASS, bpIdFromPath, clearRun, fillRoute, hasUnfilledParams, isFinished, loadRun, parseAdvance, routePath, runReducer, saveRun, stripOptionalParams } from './workflowRunner'
import TourSpotlight, { otherLayerOpen } from './TourSpotlight'
import WorkflowBar, { BAR_HEIGHT, barGap } from './WorkflowBar'
import WorkflowBuildingPicker from './WorkflowBuildingPicker'

const queryMissing = (target, search) => {
  const query = target.slice(routePath(target).length).replace(/^\?/, '')
  if (!query) return null
  const wanted = new URLSearchParams(query)
  const current = new URLSearchParams(search)
  let missing = false
  wanted.forEach((value, key) => {
    if (current.get(key) !== value) missing = true
    current.set(key, value)
  })
  return missing ? current.toString() : null
}

const WorkflowContext = createContext({ startWorkflow: () => {}, stopWorkflow: () => {}, activeWorkflowKey: null })

const DEFAULT_WAIT = 10000
const OPTIONAL_WAIT = 3000

const AUTH_PATHS = [PATHS.login, PATHS.signup, PATHS.emailVerification, PATHS.requestPasswordReset, PATHS.passwordReset, PATHS.sharepointCallback, '/share/guru', PATHS.twoFactorSetup]

const isAuthPath = pathname => AUTH_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`))

const isBlockedForGuide = (pathname, key) =>
  isAuthPath(pathname) && !(pathname === PATHS.twoFactorSetup && key === 'getting-started.two-factor')

const PAGE_NAMES = [
  [/^\/tasks(\/|$)/, 'Tasks'],
  [/^\/actions(\/|$)/, 'Remedials'],
  [/^\/bp-list\/[^/]+\/files(\/|$)/, 'Documents'],
  [/^\/files\/upload(\/|$)/, 'the upload page'],
  [/^\/access-requests(\/|$)/, 'your access requests'],
  [/^\/portfolios(\/|$)/, 'your Building Passports'],
  [/^\/contract-management(\/|$)/, 'Contract management'],
  [/^\/bp-list\//, 'the Building Passport'],
]

const pageName = path => {
  const found = PAGE_NAMES.find(([pattern]) => pattern.test(path))
  return found ? found[1] : 'the right page'
}

const resumeSaved = () => {
  const saved = loadRun()
  const definition = saved && getWorkflowByKey(saved.key)
  if (!definition || !saved.userId) return null
  if ((saved.version || 1) !== (definition.version || 1) || saved.stepIndex >= definition.steps.length) return null
  return runReducer(null, { type: 'resume', run: saved })
}

export const WorkflowProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentUser = useSelector(getCurrentUser)
  const userId = currentUser?.id || null
  const [run, dispatch] = useReducer(runReducer, null, () => {
    const resumed = resumeSaved()
    if (resumed) document.documentElement.classList.add(RUNNING_CLASS)
    return resumed
  })
  const [anchorEl, setAnchorEl] = useState(null)
  const [askStop, setAskStop] = useState(0)
  const [barHeight, setBarHeight] = useState(BAR_HEIGHT - 16)
  const [barDocked, setBarDocked] = useState(false)
  const userIdRef = useRef(userId)
  userIdRef.current = userId
  const runRef = useRef(run)
  runRef.current = run
  const locationRef = useRef(location)
  locationRef.current = location
  const prevUserIdRef = useRef(userId)
  const backPathRef = useRef(null)
  const navKeyRef = useRef(null)

  const resetGuards = useCallback(() => {
    backPathRef.current = null
    navKeyRef.current = null
  }, [])

  const workflow = run ? getWorkflowByKey(run.key) : null
  const step = workflow && run && !isFinished(run) ? workflow.steps[run.stepIndex] : null
  const active = !!run && !!userId && run.userId === userId && !isBlockedForGuide(location.pathname, run.key)

  const record = useCallback((status, extra) => {
    const current = runRef.current
    if (!current || !userIdRef.current || current.userId !== userIdRef.current) return
    markTourProgressRemote(current.key, status, current.version, extra).catch(() => {})
  }, [])

  const complete = useCallback(completedVia => {
    const current = runRef.current
    if (!current) return
    record('completed', { lastStep: Math.min(current.stepIndex, current.stepCount - 1), completedVia })
    markTourSeen(userId, current.key, 'completed', current.version)
    resetGuards()
    setAnchorEl(null)
    setAskStop(0)
    dispatch({ type: 'stop' })
  }, [record, userId, resetGuards])

  const startWorkflow = useCallback((key, opts = {}) => {
    const next = getWorkflowByKey(key)
    const owner = userIdRef.current
    if (!next || !next.steps?.length || !owner) return
    const bpId = opts.bpId || bpIdFromPath(locationRef.current.pathname)
    resetGuards()
    setAnchorEl(null)
    setAskStop(0)
    dispatch({ type: 'start', workflow: next, ctx: { bpId, userId: owner } })
    markTourProgressRemote(next.key, 'started', next.version || 1, { lastStep: 0 }).catch(() => {})
  }, [resetGuards])

  const stopWorkflow = useCallback(() => {
    const current = runRef.current
    if (!current) return
    if (getWorkflowByKey(current.key)?.steps?.[current.stepIndex]?.stop) {
      complete('stopped_at_end')
      return
    }
    record('stopped', { lastStep: current.stepIndex })
    resetGuards()
    setAnchorEl(null)
    setAskStop(0)
    dispatch({ type: 'stop' })
  }, [record, resetGuards, complete])

  useEffect(() => {
    if (run) saveRun(run)
    else clearRun()
    document.documentElement.classList.toggle(RUNNING_CLASS, !!run)
  }, [run])

  useEffect(() => () => document.documentElement.classList.remove(RUNNING_CLASS), [])

  useEffect(() => {
    const previous = prevUserIdRef.current
    prevUserIdRef.current = userId
    const current = runRef.current
    if (!current) return
    if ((previous && previous !== userId) || (userId && current.userId !== userId)) {
      resetGuards()
      setAnchorEl(null)
      setAskStop(0)
      dispatch({ type: 'stop' })
    }
  }, [userId, resetGuards])

  useEffect(() => {
    if (backPathRef.current && backPathRef.current !== location.pathname) backPathRef.current = null
  }, [location.pathname])

  const goBack = useCallback(() => {
    backPathRef.current = locationRef.current.pathname
    navKeyRef.current = null
    dispatch({ type: 'back' })
  }, [])

  useEffect(() => {
    if (active && isFinished(run)) complete('stopped_at_end')
  }, [active, run, complete])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const key = params.get('guide')
    if (!key || !userId || isBlockedForGuide(location.pathname, key)) return
    params.delete('guide')
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
    startWorkflow(key)
  }, [location.search, location.pathname, navigate, startWorkflow, userId])

  useEffect(() => {
    if (!active || run.phase !== 'locating' || !step) return undefined
    const pending = parseAdvance(step.advance)
    const cameBack = backPathRef.current === location.pathname
    if (!cameBack && pending.type === 'route' && matchesRoute(routePath(pending.pattern), location.pathname) && !(step.route && matchesRoute(routePath(step.route), location.pathname))) {
      dispatch({ type: 'advance', from: run.stepIndex })
      return undefined
    }
    if (step.route) {
      const target = stripOptionalParams(fillRoute(step.route, run.ctx))
      const navKey = `${run.key}|${run.stepIndex}|${target}`
      if (!hasUnfilledParams(target) && navKeyRef.current !== navKey) {
        if (!matchesRoute(routePath(step.route), location.pathname)) {
          navKeyRef.current = navKey
          navigate(target)
          return undefined
        }
        const search = queryMissing(target, location.search)
        if (search !== null) {
          navKeyRef.current = navKey
          navigate({ pathname: location.pathname, search: `?${search}` })
          return undefined
        }
      }
    }
    const requireEnabled = !step.stop && pending.type === 'click'
    const watch = waitForAnchor(step.anchor, step.waitMs ?? (step.optional && !step.missingText ? OPTIONAL_WAIT : DEFAULT_WAIT), requireEnabled)
    let live = true
    watch.then(el => {
      if (!live) return
      if (el) {
        setAnchorEl(el)
        dispatch({ type: 'located', from: run.stepIndex })
      } else if (step.optional && !step.missingText) {
        setAnchorEl(null)
        dispatch({ type: 'advance', from: run.stepIndex })
      } else {
        setAnchorEl(null)
        dispatch({ type: 'missing' })
        if (!step.optional) record('missing_step', { lastStep: runRef.current?.stepIndex ?? 0 })
      }
    })
    return () => {
      live = false
      watch.cancel()
    }
  }, [active, run, step, location.pathname, location.search, navigate, record])

  useEffect(() => {
    if (!active || run.phase !== 'missing' || !step) return undefined
    const from = run.stepIndex
    const pending = parseAdvance(step.advance)
    const requireEnabled = !step.stop && pending.type === 'click'
    const watch = waitForAnchor(step.anchor, null, requireEnabled)
    let live = true
    watch.then(el => {
      if (!live || !el) return
      setAnchorEl(el)
      dispatch({ type: 'located', from })
    })
    return () => {
      live = false
      watch.cancel()
    }
  }, [active, run, step])

  useEffect(() => {
    if (!active || run.phase !== 'showing' || !step || !anchorEl) return undefined
    const from = run.stepIndex
    const requireEnabled = !step.stop && parseAdvance(step.advance).type === 'click'
    const check = () => {
      const disabled = requireEnabled && (anchorEl.disabled || anchorEl.getAttribute('aria-disabled') === 'true')
      if (!disabled && anchorEl.isConnected && anchorEl.getAttribute('data-tour-id') === step.anchor) return
      setAnchorEl(null)
      dispatch({ type: 'relocate', from })
    }
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tour-id', 'disabled', 'aria-disabled'] })
    check()
    return () => observer.disconnect()
  }, [active, run, step, anchorEl])

  useEffect(() => {
    if (!active || run.phase !== 'showing' || !step || !anchorEl) return undefined
    const from = run.stepIndex
    const key = run.key
    const advance = parseAdvance(step.advance)
    if (step.stop || advance.type === 'click') {
      const selector = anchorSelector(step.anchor)
      let fired = false
      const onClick = e => {
        const target = e.target instanceof Element ? e.target : null
        if (fired || !target || !target.closest(selector)) return
        fired = true
        setTimeout(() => {
          const current = runRef.current
          if (!current || current.key !== key || current.stepIndex !== from) return
          if (step.stop) complete('saved')
          else dispatch({ type: 'advance', from })
        }, 0)
      }
      const onTouch = e => {
        const target = e.target instanceof Element ? e.target : null
        if (fired || !target || !target.closest(selector)) return
        setTimeout(() => {
          if (fired || anchorEl.isConnected) return
          fired = true
          const current = runRef.current
          if (!current || current.key !== key || current.stepIndex !== from) return
          if (step.stop) complete('saved')
          else dispatch({ type: 'advance', from })
        }, 0)
      }
      document.addEventListener('click', onClick, true)
      document.addEventListener('touchstart', onTouch, true)
      return () => {
        document.removeEventListener('click', onClick, true)
        document.removeEventListener('touchstart', onTouch, true)
      }
    }
    if (advance.type === 'appear') {
      const watch = waitForAnchor(advance.anchor, null, false)
      watch.then(el => { if (el) dispatch({ type: 'advance', from }) })
      return () => watch.cancel()
    }
    return undefined
  }, [active, run, step, anchorEl, complete])

  useEffect(() => {
    if (!active || (run.phase !== 'showing' && run.phase !== 'missing') || !step) return
    const advance = parseAdvance(step.advance)
    if (backPathRef.current === location.pathname) return
    if (advance.type === 'route' && matchesRoute(routePath(advance.pattern), location.pathname)) dispatch({ type: 'advance', from: run.stepIndex })
  }, [active, run, step, location.pathname])

  useEffect(() => {
    setBarDocked(false)
  }, [run?.key, run?.stepIndex])

  const [layerOpen, setLayerOpen] = useState(false)
  const watchLayers = active && run.phase !== 'showing' && run.phase !== 'choosing'
  useEffect(() => {
    if (!watchLayers) {
      setLayerOpen(false)
      return undefined
    }
    const check = () => setLayerOpen(otherLayerOpen())
    check()
    const timer = setInterval(check, 400)
    return () => clearInterval(timer)
  }, [watchLayers])

  const value = useMemo(() => ({ startWorkflow, stopWorkflow, activeWorkflowKey: run?.key || null }), [startWorkflow, stopWorkflow, run])

  const showSpotlight = active && run.phase === 'showing' && step && anchorEl
  const advanceType = step ? parseAdvance(step.advance).type : 'next'
  const bottomInset = barHeight + barGap()
  const navigatingTo = active && run.phase === 'locating' && step?.route && !matchesRoute(routePath(step.route), location.pathname)
    ? pageName(stripOptionalParams(fillRoute(step.route, run.ctx)))
    : null
  const status = active && run.phase === 'locating'
    ? (navigatingTo ? `Taking you to ${navigatingTo}…` : 'Finding the next step…')
    : null

  return (
    <WorkflowContext.Provider value={value}>
      {children}
      {active && run.phase === 'choosing' && workflow && (
        <WorkflowBuildingPicker title={workflow.title} onChoose={bpId => dispatch({ type: 'chooseBuilding', bpId })} />
      )}
      {showSpotlight && (
        <TourSpotlight
          mode='workflow'
          step={step}
          anchorEl={anchorEl}
          stepNumber={run.stepIndex + 1}
          stepCount={run.stepCount}
          advanceType={advanceType}
          isStop={!!step.stop}
          bottomOffset={bottomInset}
          docked={barDocked}
          onLowAnchor={setBarDocked}
          onNext={() => dispatch({ type: 'advance', from: run.stepIndex })}
          onBack={goBack}
          onFinish={() => complete('stopped_at_end')}
          onEscape={() => setAskStop(n => n + 1)}
          onDismiss={() => setAskStop(n => n + 1)}
          onCtaClick={() => dispatch({ type: 'advance', from: run.stepIndex })}
        />
      )}
      {active && workflow && (
        <WorkflowBar
          key={`${run.key}-${askStop}`}
          title={workflow.title}
          stepNumber={run.stepIndex + 1}
          stepCount={run.stepCount}
          missing={run.phase === 'missing'}
          missingText={step?.missingText}
          optional={!!step?.optional}
          escapeToStop={!showSpotlight}
          status={status}
          canBack={run.stepIndex > 0 && run.phase !== 'choosing'}
          canSkip={run.phase === 'missing' ? !!step?.optional : !!step?.optional && run.phase === 'showing'}
          onBack={goBack}
          onSkip={() => dispatch({ type: 'advance', from: run.stepIndex })}
          raised={run.phase !== 'choosing'}
          onStop={stopWorkflow}
          onKeepGoing={() => setAskStop(0)}
          onMeasure={setBarHeight}
          docked={showSpotlight ? barDocked : layerOpen}
          startConfirming={askStop > 0}
        />
      )}
    </WorkflowContext.Provider>
  )
}

export const useWorkflows = () => useContext(WorkflowContext)

export default WorkflowProvider
